import * as THREE from 'three';
import WebXRPolyfill from 'webxr-polyfill';

class AREngine {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.xrSession = null;
    this.videoElement = null;
    this.isInitialized = false;
    this.overlayElements = [];
    this.polyfill = null;
  }

  async initialize(containerElement) {
    try {
      // Initialize WebXR polyfill for fallback support
      if (!navigator.xr) {
        this.polyfill = new WebXRPolyfill();
      }

      // Create Three.js scene
      this.scene = new THREE.Scene();
      
      // Create camera
      this.camera = new THREE.PerspectiveCamera(
        75,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      this.camera.position.z = 5;

      // Create WebGL renderer
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        preserveDrawingBuffer: true
      });
      this.renderer?.setSize(window.innerWidth, window.innerHeight);
      this.renderer?.setPixelRatio(window.devicePixelRatio);
      this.renderer.xr.enabled = true;
      
      containerElement?.appendChild(this.renderer?.domElement);

      // Add ambient lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
      this.scene?.add(ambientLight);

      // Add directional light
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
      directionalLight?.position?.set(0, 1, 1);
      this.scene?.add(directionalLight);

      this.isInitialized = true;
      return { success: true };
    } catch (error) {
      console.error('AR Engine initialization error:', error);
      return { success: false, error: error?.message };
    }
  }

  async startCameraAccess() {
    try {
      // Request camera permission via WebRTC
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      // Create video element for camera feed
      this.videoElement = document.createElement('video');
      this.videoElement.srcObject = stream;
      this.videoElement?.setAttribute('playsinline', '');
      this.videoElement?.play();

      // Create video texture for Three.js
      const videoTexture = new THREE.VideoTexture(this.videoElement);
      videoTexture.minFilter = THREE?.LinearFilter;
      videoTexture.magFilter = THREE?.LinearFilter;

      // Create background plane with video texture
      const geometry = new THREE.PlaneGeometry(16, 9);
      const material = new THREE.MeshBasicMaterial({
        map: videoTexture,
        side: THREE.DoubleSide
      });
      const videoPlane = new THREE.Mesh(geometry, material);
      videoPlane.position.z = -10;
      this.scene?.add(videoPlane);

      return { success: true, stream };
    } catch (error) {
      console.error('Camera access error:', error);
      return { success: false, error: error?.message };
    }
  }

  async startXRSession() {
    try {
      if (!navigator.xr) {
        throw new Error('WebXR not supported');
      }

      const isSupported = await navigator.xr?.isSessionSupported('immersive-ar');
      if (!isSupported) {
        throw new Error('AR not supported on this device');
      }

      this.xrSession = await navigator.xr?.requestSession('immersive-ar', {
        requiredFeatures: ['hit-test', 'dom-overlay'],
        optionalFeatures: ['local-floor', 'bounded-floor']
      });

      await this.renderer?.xr?.setSession(this.xrSession);
      return { success: true };
    } catch (error) {
      console.error('XR session error:', error);
      return { success: false, error: error?.message };
    }
  }

  createOverlayPanel(position, data) {
    // Create 3D panel for financial data overlay
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas?.getContext('2d');

    // Draw panel background
    context.fillStyle = 'rgba(255, 255, 255, 0.95)';
    context?.roundRect(0, 0, 512, 512, 20);
    context?.fill();

    // Draw border
    context.strokeStyle = data?.riskColor || '#10b981';
    context.lineWidth = 4;
    context?.roundRect(0, 0, 512, 512, 20);
    context?.stroke();

    // Draw text content
    context.fillStyle = '#1f2937';
    context.font = 'bold 32px Arial';
    context?.fillText(data?.title || 'Product', 40, 60);

    context.font = '24px Arial';
    context.fillStyle = '#6b7280';
    context?.fillText(`Cost: $${data?.cost || 0}`, 40, 120);
    context?.fillText(`Budget: $${data?.budget || 0}`, 40, 160);
    context?.fillText(`Remaining: $${data?.remaining || 0}`, 40, 200);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide
    });

    const panel = new THREE.Mesh(geometry, material);
    panel?.position?.set(position?.x, position?.y, position?.z);
    this.scene?.add(panel);
    this.overlayElements?.push(panel);

    return panel;
  }

  updateOverlayPanel(panel, data) {
    if (!panel || !panel?.material || !panel?.material?.map) return;

    const canvas = panel?.material?.map?.image;
    const context = canvas?.getContext('2d');

    // Clear canvas
    context?.clearRect(0, 0, 512, 512);

    // Redraw with updated data
    context.fillStyle = 'rgba(255, 255, 255, 0.95)';
    context?.roundRect(0, 0, 512, 512, 20);
    context?.fill();

    context.strokeStyle = data?.riskColor || '#10b981';
    context.lineWidth = 4;
    context?.roundRect(0, 0, 512, 512, 20);
    context?.stroke();

    context.fillStyle = '#1f2937';
    context.font = 'bold 32px Arial';
    context?.fillText(data?.title || 'Product', 40, 60);

    context.font = '24px Arial';
    context.fillStyle = '#6b7280';
    context?.fillText(`Cost: $${data?.cost || 0}`, 40, 120);
    context?.fillText(`Budget: $${data?.budget || 0}`, 40, 160);
    context?.fillText(`Remaining: $${data?.remaining || 0}`, 40, 200);
    context?.fillText(`Impact: ${data?.impact || 'Low'}`, 40, 240);

    panel.material.map.needsUpdate = true;
  }

  clearOverlays() {
    this.overlayElements?.forEach(element => {
      this.scene?.remove(element);
      if (element?.geometry) element?.geometry?.dispose();
      if (element?.material) {
        if (element?.material?.map) element?.material?.map?.dispose();
        element?.material?.dispose();
      }
    });
    this.overlayElements = [];
  }

  startRenderLoop() {
    const animate = () => {
      this.renderer?.setAnimationLoop(() => {
        this.renderer?.render(this.scene, this.camera);
      });
    };
    animate();
  }

  stopRenderLoop() {
    this.renderer?.setAnimationLoop(null);
  }

  async stopCamera() {
    if (this.videoElement && this.videoElement?.srcObject) {
      const tracks = this.videoElement?.srcObject?.getTracks();
      tracks?.forEach(track => track?.stop());
      this.videoElement.srcObject = null;
    }
  }

  async cleanup() {
    try {
      this.stopRenderLoop();
      await this.stopCamera();
      
      if (this.xrSession) {
        await this.xrSession?.end();
        this.xrSession = null;
      }

      this.clearOverlays();

      if (this.renderer) {
        this.renderer?.dispose();
        if (this.renderer?.domElement && this.renderer?.domElement?.parentNode) {
          this.renderer?.domElement?.parentNode?.removeChild(this.renderer?.domElement);
        }
      }

      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.isInitialized = false;
    } catch (error) {
      console.error('AR Engine cleanup error:', error);
    }
  }

  handleResize() {
    if (!this.camera || !this.renderer) return;
    
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera?.updateProjectionMatrix();
    this.renderer?.setSize(window.innerWidth, window.innerHeight);
  }
}

export default AREngine;
